import { useId, type SVGProps } from "react";

// 내부 Asset 상세(Figma ver.7 03-09~12) 전용 아이콘 — 시안의 공식 SVG 경로예요 (front_icon_*.svg, 2026-10-14 수집).
// 선 색은 currentColor 로 바꿨고(시안은 #FD5108 · 흰색), 굵기 · 격자는 시안 그대로라서 격자 크기(size 기본값)로 쓰면 시안과 같아요.
type IconProps = Omit<SVGProps<SVGSVGElement>, "width" | "height"> & { size?: number };

function Icon({ size, grid, children, ...props }: IconProps & { size: number; grid: number }) {
  return (
    <svg width={size} height={size} viewBox={`0 0 ${grid} ${grid}`} fill="none" aria-hidden {...props}>
      {children}
    </svg>
  );
}

const S = { stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" } as const;

export const NvVideoIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M11.6667 5H4.16667C3.24619 5 2.5 5.74619 2.5 6.66667V13.3333C2.5 14.2538 3.24619 15 4.16667 15H11.6667C12.5871 15 13.3333 14.2538 13.3333 13.3333V6.66667C13.3333 5.74619 12.5871 5 11.6667 5Z" {...S} />
    <path d="M13.3359 8.75L17.5026 6.25V13.75L13.3359 11.25V8.75Z" {...S} />
  </Icon>
);

export const NvMonitorIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M15.8333 3.33301H4.16667C3.24619 3.33301 2.5 4.0792 2.5 4.99967V11.6663C2.5 12.5868 3.24619 13.333 4.16667 13.333H15.8333C16.7538 13.333 17.5 12.5868 17.5 11.6663V4.99967C17.5 4.0792 16.7538 3.33301 15.8333 3.33301Z" {...S} />
    <path d="M6.66406 16.6663H13.3307M9.9974 13.333V16.6663" {...S} />
  </Icon>
);

export const NvPulseIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M2.5 9.99967H5.83333L8.33333 3.33301L11.6667 16.6663L14.1667 9.99967H17.5" {...S} />
  </Icon>
);

export const NvFileIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M11.6641 2.5H5.83073C5.3887 2.5 4.96478 2.67559 4.65222 2.98816C4.33966 3.30072 4.16406 3.72464 4.16406 4.16667V15.8333C4.16406 16.2754 4.33966 16.6993 4.65222 17.0118C4.96478 17.3244 5.3887 17.5 5.83073 17.5H14.1641C14.6061 17.5 15.03 17.3244 15.3426 17.0118C15.6551 16.6993 15.8307 16.2754 15.8307 15.8333V6.66667L11.6641 2.5Z" {...S} />
    <path d="M11.6641 2.5V6.66667H15.8307" {...S} />
  </Icon>
);

export const NvFileOkIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M11.6641 2.5H5.83073C5.3887 2.5 4.96478 2.67559 4.65222 2.98816C4.33966 3.30072 4.16406 3.72464 4.16406 4.16667V15.8333C4.16406 16.2754 4.33966 16.6993 4.65222 17.0118C4.96478 17.3244 5.3887 17.5 5.83073 17.5H14.1641C14.6061 17.5 15.03 17.3244 15.3426 17.0118C15.6551 16.6993 15.8307 16.2754 15.8307 15.8333V6.66667L11.6641 2.5Z" {...S} />
    <path d="M11.6641 2.5V6.66667H15.8307" {...S} />
    <path d="M7.5 12.0837L9.16667 13.7503L12.5 10.417" {...S} />
  </Icon>
);

// 상태 칩 안의 작은 체크 (보유 · 가능)
export const NvCheckSmallIcon = ({ size = 14, ...p }: IconProps) => (
  <Icon size={size} grid={14} {...p}>
    <path d="M2.91406 7.29167L5.53906 9.91667L11.0807 4.375" {...S} />
  </Icon>
);

export const NvArrowRightIcon = ({ size = 18, ...p }: IconProps) => (
  <Icon size={size} grid={18} {...p}>
    <path d="M3.75 9H14.25M9.75 13.5L14.25 9L9.75 4.5" {...S} />
  </Icon>
);

// 채운 주황 원 + 흰 체크 (소스코드 보유 · 제출 가능)
export const NvCheckCircleIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <rect width="20" height="20" rx="10" fill="currentColor" />
    <path d="M6.21094 10.2708L8.64844 12.7083L13.7943 7.5625" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </Icon>
);

export const NvCodeIcon = ({ size = 16, ...p }: IconProps) => (
  <Icon size={size} grid={16} {...p}>
    <path d="M5.33073 5.33398L2.66406 8.00065L5.33073 10.6673M10.6641 5.33398L13.3307 8.00065L10.6641 10.6673M8.9974 3.33398L6.9974 12.6673" {...S} />
  </Icon>
);

export const NvKeyIcon = ({ size = 16, ...p }: IconProps) => (
  <Icon size={size} grid={16} {...p}>
    <path d="M5.33073 12.6673C6.80349 12.6673 7.9974 11.4734 7.9974 10.0007C7.9974 8.52789 6.80349 7.33398 5.33073 7.33398C3.85797 7.33398 2.66406 8.52789 2.66406 10.0007C2.66406 11.4734 3.85797 12.6673 5.33073 12.6673Z" {...S} />
    <path d="M7.33594 8L13.3359 2M10.6693 4.66667L12.6693 6.66667" {...S} />
  </Icon>
);

export const NvFolderIcon = ({ size = 16, ...p }: IconProps) => (
  <Icon size={size} grid={16} {...p}>
    <path d="M2 3.99935C2 3.64573 2.14048 3.30659 2.39052 3.05654C2.64057 2.80649 2.97971 2.66602 3.33333 2.66602H6L7.33333 3.99935H12.6667C13.0203 3.99935 13.3594 4.13982 13.6095 4.38987C13.8595 4.63992 14 4.97906 14 5.33268V11.9993C14 12.353 13.8595 12.6921 13.6095 12.9422C13.3594 13.1922 13.0203 13.3327 12.6667 13.3327H3.33333C2.97971 13.3327 2.64057 13.1922 2.39052 12.9422C2.14048 12.6921 2 12.353 2 11.9993V3.99935Z" {...S} />
  </Icon>
);

export const NvSendIcon = ({ size = 16, ...p }: IconProps) => (
  <Icon size={size} grid={16} {...p}>
    <path d="M14 2L6.66667 9.33333M6.66667 9.33333L2 6.66667L14 2L9.33333 14L6.66667 9.33333Z" {...S} />
  </Icon>
);

export const NvPageIcon = ({ size = 16, ...p }: IconProps) => (
  <Icon size={size} grid={16} {...p}>
    <path d="M9.33594 2H4.66927C4.31565 2 3.97651 2.14048 3.72646 2.39052C3.47641 2.64057 3.33594 2.97971 3.33594 3.33333V12.6667C3.33594 13.0203 3.47641 13.3594 3.72646 13.6095C3.97651 13.8595 4.31565 14 4.66927 14H11.3359C11.6896 14 12.0287 13.8595 12.2787 13.6095C12.5288 13.3594 12.6693 13.0203 12.6693 12.6667V5.33333L9.33594 2Z" {...S} />
    <path d="M9.33594 2V5.33333H12.6693" {...S} />
  </Icon>
);

export const NvServerIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M15.8333 3.33398H4.16667C3.24619 3.33398 2.5 4.08018 2.5 5.00065V7.50065C2.5 8.42113 3.24619 9.16732 4.16667 9.16732H15.8333C16.7538 9.16732 17.5 8.42113 17.5 7.50065V5.00065C17.5 4.08018 16.7538 3.33398 15.8333 3.33398Z" {...S} />
    <path d="M15.8333 10.834H4.16667C3.24619 10.834 2.5 11.5802 2.5 12.5007V15.0007C2.5 15.9211 3.24619 16.6673 4.16667 16.6673H15.8333C16.7538 16.6673 17.5 15.9211 17.5 15.0007V12.5007C17.5 11.5802 16.7538 10.834 15.8333 10.834Z" {...S} />
    <path d="M5.83594 6.25H5.84427M5.83594 13.75H5.84427" {...S} />
  </Icon>
);

export const NvGlobeIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5Z" {...S} />
    <path d="M2.5 10H17.5M10 2.5C12.0833 4.58333 13.1667 7.08333 13.1667 10C13.1667 12.9167 12.0833 15.4167 10 17.5C7.91667 15.4167 6.83333 12.9167 6.83333 10C6.83333 7.08333 7.91667 4.58333 10 2.5Z" {...S} />
  </Icon>
);

// 용어집 · 목록 문서
export const NvStackDocIcon = ({ size = 24, ...p }: IconProps) => (
  <Icon size={size} grid={24} {...p}>
    <path d="M8 3H16L20 7V17C20 17.5304 19.7893 18.0391 19.4142 18.4142C19.0391 18.7893 18.5304 19 18 19H8C7.46957 19 6.96086 18.7893 6.58579 18.4142C6.21071 18.0391 6 17.5304 6 17V5C6 4.46957 6.21071 3.96086 6.58579 3.58579C6.96086 3.21071 7.46957 3 8 3Z" {...S} />
    <path d="M3 7V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H15" {...S} />
  </Icon>
);

export const NvUsersIcon = ({ size = 24, ...p }: IconProps) => (
  <Icon size={size} grid={24} {...p}>
    <path d="M9 11.5C10.933 11.5 12.5 9.933 12.5 8C12.5 6.067 10.933 4.5 9 4.5C7.067 4.5 5.5 6.067 5.5 8C5.5 9.933 7.067 11.5 9 11.5Z" {...S} />
    <path d="M2.5 20C2.5 16.4 5.4 14 9 14C12.6 14 15.5 16.4 15.5 20" {...S} />
    <path d="M17 11.5C18.3807 11.5 19.5 10.3807 19.5 9C19.5 7.61929 18.3807 6.5 17 6.5C15.6193 6.5 14.5 7.61929 14.5 9C14.5 10.3807 15.6193 11.5 17 11.5Z" {...S} />
    <path d="M17 14C19.6 14 21.5 15.8 21.5 18.5" {...S} />
  </Icon>
);

export const NvPersonOutlineIcon = ({ size = 24, ...p }: IconProps) => (
  <Icon size={size} grid={24} {...p}>
    <path d="M12 12C14.2091 12 16 10.2091 16 8C16 5.79086 14.2091 4 12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12Z" {...S} />
    <path d="M4 21C4 16.6 7.6 14 12 14C16.4 14 20 16.6 20 21" {...S} />
  </Icon>
);

export const NvCopyIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M15.8307 6.66602H8.33073C7.41025 6.66602 6.66406 7.41221 6.66406 8.33268V15.8327C6.66406 16.7532 7.41025 17.4993 8.33073 17.4993H15.8307C16.7512 17.4993 17.4974 16.7532 17.4974 15.8327V8.33268C17.4974 7.41221 16.7512 6.66602 15.8307 6.66602Z" {...S} />
    <path d="M13.3333 6.66667V4.16667C13.3333 3.72464 13.1577 3.30072 12.8452 2.98816C12.5326 2.67559 12.1087 2.5 11.6667 2.5H4.16667C3.72464 2.5 3.30072 2.67559 2.98816 2.98816C2.67559 3.30072 2.5 3.72464 2.5 4.16667V11.6667C2.5 12.1087 2.67559 12.5326 2.98816 12.8452C3.30072 13.1577 3.72464 13.3333 4.16667 13.3333H6.66667" {...S} />
  </Icon>
);

export const NvCalendarIcon = ({ size = 24, ...p }: IconProps) => (
  <Icon size={size} grid={24} {...p}>
    <path d="M19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5Z" {...S} />
    <path d="M3 10H21M8 3V7M16 3V7" {...S} />
  </Icon>
);

export const NvFlagIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M4.16406 17.5007V3.33398H14.1641L12.0807 6.66732L14.1641 10.0007H4.16406" {...S} />
  </Icon>
);

export const NvShieldCheckIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M10.0026 2.5L16.6693 5V10C16.6693 13.75 13.8359 16.6667 10.0026 17.5C6.16927 16.6667 3.33594 13.75 3.33594 10V5L10.0026 2.5Z" {...S} />
    <path d="M7.5 10.0007L9.16667 11.6673L12.5 8.33398" {...S} />
  </Icon>
);

export const NvBoxIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M17.5 6.66667L10 2.5L2.5 6.66667V13.3333L10 17.5L17.5 13.3333V6.66667Z" {...S} />
    <path d="M2.5 6.66602L10 10.8327M10 10.8327L17.5 6.66602M10 10.8327V17.4993" {...S} />
  </Icon>
);

export const NvPriceTagIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M2.5 10V3.33333C2.5 3.11232 2.5878 2.90036 2.74408 2.74408C2.90036 2.5878 3.11232 2.5 3.33333 2.5H10L17.5 10L10 17.5L2.5 10Z" {...S} />
    <path d="M6.25 7.5C6.94036 7.5 7.5 6.94036 7.5 6.25C7.5 5.55964 6.94036 5 6.25 5C5.55964 5 5 5.55964 5 6.25C5 6.94036 5.55964 7.5 6.25 7.5Z" {...S} />
  </Icon>
);

export const NvLockOutlineIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M14.1641 9.16602H5.83073C4.91025 9.16602 4.16406 9.91221 4.16406 10.8327V15.8327C4.16406 16.7532 4.91025 17.4993 5.83073 17.4993H14.1641C15.0845 17.4993 15.8307 16.7532 15.8307 15.8327V10.8327C15.8307 9.91221 15.0845 9.16602 14.1641 9.16602Z" {...S} />
    <path d="M6.66406 9.16667V5.83333C6.66406 4.94928 7.01525 4.10143 7.64037 3.47631C8.26549 2.85119 9.11334 2.5 9.9974 2.5C10.8815 2.5 11.7293 2.85119 12.3544 3.47631C12.9795 4.10143 13.3307 4.94928 13.3307 5.83333V9.16667" {...S} />
  </Icon>
);

// 보안등급 배지 안의 흰 자물쇠 (14)
export const NvLockBadgeIcon = ({ size = 14, ...p }: IconProps) => (
  <Icon size={size} grid={14} {...p}>
    <path d="M4.08333 6.41644V4.08292C4.08333 3.30931 4.39062 2.56738 4.93761 2.02036C5.48459 1.47333 6.22645 1.16602 7 1.16602C7.77355 1.16602 8.51541 1.47333 9.06239 2.02036C9.60938 2.56738 9.91667 3.30931 9.91667 4.08292V6.41644M2.91667 6.41644H11.0833C11.7277 6.41644 12.25 6.93881 12.25 7.5832V11.6669C12.25 12.3112 11.7277 12.8336 11.0833 12.8336H2.91667C2.27233 12.8336 1.75 12.3112 1.75 11.6669V7.5832C1.75 6.93881 2.27233 6.41644 2.91667 6.41644Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </Icon>
);

// 보안등급 툴팁을 여는 정보 아이콘 (18)
export const NvInfoRingIcon = ({ size = 18, ...p }: IconProps) => (
  <Icon size={size} grid={18} {...p}>
    <path
      d="M1.125 9.02834C1.125 4.68597 4.65765 1.15332 9 1.15332C13.3423 1.15332 16.875 4.68597 16.875 9.02834C16.875 13.1808 13.6423 16.5844 9.5625 16.8748V15.7498C13.0218 15.4624 15.75 12.5603 15.75 9.02834C15.75 5.30643 12.7222 2.27832 9 2.27832C5.27783 2.27832 2.25 5.30643 2.25 9.02834C2.25 12.5348 4.93808 15.4231 8.36168 15.746L7.63942 16.7774C3.9445 16.13 1.125 12.9059 1.125 9.02834ZM9.5625 8.25744H8.4375V12.7745H9.5625V8.25744ZM9.5625 5.84021H8.4375V6.96521H9.5625V5.84021Z"
      fill="currentColor"
    />
  </Icon>
);

export const NvPlayIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M7 4.5V15.5L16 10L7 4.5Z" fill="currentColor" />
  </Icon>
);

export const NvPauseIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <rect x="4" y="3" width="4" height="14" rx="1" fill="currentColor" />
    <rect x="12" y="3" width="4" height="14" rx="1" fill="currentColor" />
  </Icon>
);

export const NvFullscreenIcon = ({ size = 20, ...p }: IconProps) => (
  <Icon size={size} grid={20} {...p}>
    <path d="M1 7V1H7M13 1H19V7M19 13V19H13M7 19H1V13" stroke="currentColor" strokeWidth="2" />
  </Icon>
);

// 데모 영상 모달 닫기 (Figma Icon / Close 18, 사각 안의 X)
export const NvCloseBoxIcon = ({ size = 18, ...p }: IconProps) => (
  <Icon size={size} grid={18} {...p}>
    <path d="M5.24627 13.5451L8.99814 9.79321L12.75 13.5451L13.5431 12.752L9.79126 9.00009L12.7163 6.07509V4.48883L8.99814 8.20696L5.24627 4.45508L4.45312 5.24819L8.20501 9.00009L4.45312 12.752L5.24627 13.5451Z" fill="currentColor" />
    <path d="M15.75 2.25H2.25V15.75H14.959L15.7349 14.6419V14.625H3.375V3.375H14.6191L14.6111 13.5H15.7364L15.7463 2.27966L15.7445 2.28031L15.75 2.25Z" fill="currentColor" />
  </Icon>
);

// 대표 이미지 기본형의 픽토그램 (돋보기 + 막대 차트, 시안 110 x 103) — 주황 그라데이션
export function NvHeroPictogram({ width = 110, height = 103, ...p }: Omit<SVGProps<SVGSVGElement>, "viewBox" | "fill">) {
  const gradientId = useId();
  return (
    <svg width={width} height={height} viewBox="0 0 21 20" preserveAspectRatio="none" fill="none" aria-hidden {...p}>
      <path
        d="M21.0016 19.0924V19.8561H0V0H0.763695V19.0924H21.0016ZM8.05675 13.157C9.0468 13.7689 10.2085 14.1284 11.4554 14.1284C15.0349 14.1284 17.9468 11.216 17.9468 7.63695C17.9468 4.31661 15.4393 1.5743 12.2191 1.19452V1.96626C15.0169 2.34156 17.1831 4.73803 17.1831 7.63695C17.1831 10.7954 14.6139 13.3647 11.4554 13.3647C8.29699 13.3647 5.72772 10.7954 5.72772 7.63695C5.72772 4.76851 7.84899 2.39301 10.604 1.97967L11.1782 1.15956C7.72785 1.30629 4.96402 4.15141 4.96402 7.63695C4.96402 9.69554 5.93042 11.5298 7.43001 12.7199L4.03747 17.565H4.97027L8.05675 13.157ZM6.87326 8.40065V9.54317C7.06503 10.0023 7.3233 10.4269 7.63695 10.8044V8.40065H6.87326ZM8.40065 5.34587V11.5416C8.63906 11.7285 8.89386 11.8948 9.16434 12.0363V5.34587H8.40065ZM9.92804 3.05478V12.358C10.1745 12.438 10.4302 12.4953 10.6917 12.536V3.05478H9.92804ZM11.4554 3.05478V12.601C11.7158 12.601 11.9695 12.5748 12.2191 12.536V3.05478H11.4554ZM12.9828 7.61608V12.358C13.248 12.272 13.5029 12.1637 13.7465 12.0363V7.61608H12.9828ZM14.5102 8.40065V11.5416C14.7896 11.3225 15.0472 11.0772 15.2739 10.8044V8.40065H14.5102Z"
        fill={`url(#${gradientId})`}
      />
      <defs>
        <linearGradient id={gradientId} x1="0.286385" y1="20.1425" x2="16.7101" y2="3.71879" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FF9F00" />
          <stop offset="0.05089" stopColor="#FE9900" />
          <stop offset="0.466323" stopColor="#FD7204" />
          <stop offset="0.797112" stopColor="#FD5907" />
          <stop offset="1" stopColor="#FD5108" />
        </linearGradient>
      </defs>
    </svg>
  );
}
